import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalCloseButton,
  useToast, Avatar,
} from "@chakra-ui/react";
import { api } from "../../lib/api";
import { FUNCTIONAL_DEPARTMENTS, FUNCTIONAL_ROLE_LABELS, FUNCTIONAL_ROLE_COLORS, TASK_PRIORITIES, PRIORITY_LABELS } from "./df-constants";
import RichTextEditor from "../../Components/RichTextEditor";

// ------- Section pills (only the spec-allowed ones) -------
const SECTION_PILLS = [
  { key: "statusSummary", label: "Task status summaries", icon: "📋" },
  { key: "files",         label: "Files",                 icon: "📎", soon: true },
  { key: "checklist",     label: "Checklists",            icon: "☑️" },
  { key: "project",       label: "Project",               icon: "📁" },
  { key: "participants",  label: "Participants",          icon: "👥" },
  { key: "tags",          label: "Tags",                  icon: "🏷️" },
  { key: "reminders",     label: "Reminders",             icon: "🔔" },
  { key: "deptSubtasks",  label: "Department subtasks",   icon: "🎯" },
  { key: "timeTracking",  label: "Time tracking",         icon: "⏱️", soon: true },
];

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default function TaskCreateModalV2({
  isOpen,
  onClose,
  projects = [],
  users = [],
  defaultProjectId,
  defaultProjectDepartmentId,
  onCreated,
}) {
  const toast = useToast();
  const meId = localStorage.getItem("userID");
  const meName = localStorage.getItem("username") || "Me";
  const titleRef = useRef(null);

  const empty = {
    title: "",
    description: "",
    status: "todo",
    priority: "normal",
    projectId: defaultProjectId || "",
    projectDepartmentId: defaultProjectDepartmentId || null,
    assigneeId: meId,
    dueDate: "",
    dueTime: "",
    labels: [],
    checklist: [], // {label, done:false, _temp:true}
    departmentSubtasks: {}, // { dept: assigneeId or "" }
    statusSummary: "", // optional first summary at create
    reminderAt: "", // ISO datetime
    reminderBody: "",
  };

  const [f, setF] = useState(empty);
  const [enabled, setEnabled] = useState({ checklist: true });
  const [labelInput, setLabelInput] = useState("");
  const [checkInput, setCheckInput] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setF(empty);
      setEnabled({ checklist: true });
      setLabelInput("");
      setCheckInput("");
      setTimeout(() => titleRef.current?.focus(), 50);
    }
    // eslint-disable-next-line
  }, [isOpen]);

  function toggleSection(key) {
    setEnabled((e) => ({ ...e, [key]: !e[key] }));
  }

  function addCheckItem() {
    const v = checkInput.trim();
    if (!v) return;
    setF((p) => ({ ...p, checklist: [...p.checklist, { _id: `tmp_${Date.now()}_${Math.random()}`, label: v, done: false }] }));
    setCheckInput("");
  }

  function removeCheckItem(id) {
    setF((p) => ({ ...p, checklist: p.checklist.filter((c) => c._id !== id) }));
  }

  function toggleCheckDone(id) {
    setF((p) => ({ ...p, checklist: p.checklist.map((c) => c._id === id ? { ...c, done: !c.done } : c) }));
  }

  function addLabel() {
    const v = labelInput.trim();
    if (!v) return;
    if (f.labels.includes(v)) { setLabelInput(""); return; }
    setF((p) => ({ ...p, labels: [...p.labels, v] }));
    setLabelInput("");
  }

  function toggleDept(d) {
    setF((p) => {
      const next = { ...p.departmentSubtasks };
      if (d in next) delete next[d];
      else next[d] = "";
      return { ...p, departmentSubtasks: next };
    });
  }

  const dueAtIso = useMemo(() => {
    if (!f.dueDate) return null;
    const t = f.dueTime || "00:00";
    return `${f.dueDate}T${t}:00`;
  }, [f.dueDate, f.dueTime]);

  async function submit() {
    if (!f.title.trim()) { toast({ title: "Title required", status: "warning" }); return; }
    setSubmitting(true);
    try {
      const departmentSubtasks = Object.entries(f.departmentSubtasks || {}).map(([department, assigneeId]) => ({
        department, assigneeId: assigneeId || null,
      }));
      const body = {
        title: f.title,
        description: f.description,
        status: f.status,
        priority: f.priority,
        projectId: f.projectId || null,
        projectDepartmentId: f.projectDepartmentId || null,
        dueDate: dueAtIso,
        assignees: f.assigneeId ? [f.assigneeId] : [],
        labels: enabled.tags ? f.labels : [],
        departmentSubtasks: enabled.deptSubtasks && departmentSubtasks.length ? departmentSubtasks : undefined,
      };
      const r = await api("/tasks", { method: "POST", body: JSON.stringify(body) });
      const taskId = r.task._id;

      // Persist checklist items
      if (enabled.checklist && f.checklist.length) {
        for (const c of f.checklist) {
          await api(`/tasks/${taskId}/checklist`, { method: "POST", body: JSON.stringify({ label: c.label }) });
          if (c.done) {
            // mark done — but we don't have the new id; refetch and toggle by index. Skip for now.
          }
        }
      }
      // Persist initial status summary (HTML from rich text)
      const summaryClean = (f.statusSummary || "").trim();
      const summaryEmpty = !summaryClean || summaryClean === "<p></p>";
      if (enabled.statusSummary && !summaryEmpty) {
        await api(`/tasks/${taskId}/status-summaries`, { method: "POST", body: JSON.stringify({ text: f.statusSummary }) });
      }
      // Persist reminder
      if (enabled.reminders && f.reminderAt) {
        await api(`/tasks/${taskId}/reminders`, {
          method: "POST",
          body: JSON.stringify({ remindAt: f.reminderAt, body: f.reminderBody }),
        });
      }

      toast({ title: "Task created", status: "success", duration: 1800 });
      onCreated && onCreated(r.task);
      onClose();
    } catch (e) {
      toast({ title: e.message || "Failed to create task", status: "error" });
    } finally { setSubmitting(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" motionPreset="slideInBottom" closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent bg="transparent" boxShadow="none" m={0} maxW="100vw" maxH="100vh">
        <div className="fixed inset-0 flex items-stretch justify-center p-2 md:p-4">
          <div className="w-full max-w-[1400px] flex flex-col md:flex-row bg-white rounded-xl shadow-2xl overflow-hidden">

            {/* LEFT — form */}
            <div className="flex-1 min-w-0 flex flex-col border-r border-ink-100">
              {/* Top bar */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
                <div className="text-xs font-semibold text-ink-500 uppercase tracking-wider">Create task</div>
                <ModalCloseButton position="static" />
              </div>

              {/* Title hero */}
              <div className="px-6 pt-5">
                <input
                  ref={titleRef}
                  value={f.title}
                  onChange={(e) => setF({ ...f, title: e.target.value })}
                  placeholder="Task name"
                  className="w-full text-2xl font-semibold text-ink-800 placeholder-ink-300 bg-transparent border-0 outline-none"
                />
              </div>

              {/* Description */}
              <div className="px-6 mt-4">
                <RichTextEditor
                  value={f.description}
                  onChange={(html) => setF((p) => ({ ...p, description: html }))}
                  placeholder="Description — use @ to mention, ** for bold, - for a list"
                  minHeight={120}
                />
                <div className="flex items-center gap-2 mt-2 text-[10px] text-ink-400">
                  <span className="ml-auto">Attachments in Phase 10c</span>
                </div>
              </div>

              {/* Owner / assignee / deadline cards */}
              <div className="px-6 mt-5">
                <div className="bg-white border border-ink-200 rounded-lg p-4 space-y-3">
                  <Field label="Task owner">
                    <div className="flex items-center gap-2">
                      <Avatar size="xs" name={meName} />
                      <span className="text-sm text-ink-700">{meName}</span>
                    </div>
                  </Field>
                  <Field label="Assignee">
                    <select
                      value={f.assigneeId || ""}
                      onChange={(e) => setF({ ...f, assigneeId: e.target.value })}
                      className="text-sm text-ink-700 bg-transparent border-0 outline-none cursor-pointer w-full"
                    >
                      <option value="">Unassigned</option>
                      {users.map((u) => <option key={u._id} value={u._id}>{u.username} ({u.email})</option>)}
                    </select>
                  </Field>
                  <Field label="Deadline">
                    <div className="flex items-center gap-2 text-sm text-ink-700">
                      <input
                        type="date"
                        value={f.dueDate}
                        onChange={(e) => setF({ ...f, dueDate: e.target.value })}
                        className="bg-transparent border-0 outline-none"
                      />
                      <input
                        type="time"
                        value={f.dueTime}
                        onChange={(e) => setF({ ...f, dueTime: e.target.value })}
                        className="bg-transparent border-0 outline-none"
                        disabled={!f.dueDate}
                      />
                      {f.dueDate && (
                        <span className="text-[10px] text-ink-400">{TZ}</span>
                      )}
                    </div>
                  </Field>
                  <Field label="Priority">
                    <div className="flex gap-2">
                      {TASK_PRIORITIES.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setF({ ...f, priority: p })}
                          className={[
                            "px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize",
                            f.priority === p
                              ? p === "urgent" ? "bg-red-500 text-white"
                                : p === "high" ? "bg-amber-500 text-white"
                                : p === "normal" ? "bg-blue-500 text-white"
                                : "bg-ink-500 text-white"
                              : "bg-ink-100 text-ink-600 hover:bg-ink-200",
                          ].join(" ")}
                        >
                          {PRIORITY_LABELS[p] || p}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </div>

              {/* Sections (collapsible / toggled by pills) */}
              <div className="px-6 mt-5 space-y-4 pb-4 overflow-y-auto flex-1">

                {enabled.checklist && (
                  <Section title="Checklists" onRemove={() => toggleSection("checklist")} icon="☑️">
                    {f.checklist.length > 0 && (
                      <div className="mb-2">
                        <div className="w-full h-1 bg-ink-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 transition-all"
                            style={{ width: `${Math.round((f.checklist.filter((c) => c.done).length / f.checklist.length) * 100)}%` }} />
                        </div>
                      </div>
                    )}
                    <ul className="space-y-1">
                      {f.checklist.map((c) => (
                        <li key={c._id} className="flex items-center gap-2 text-sm bg-ink-50 rounded px-2 py-1.5">
                          <input type="checkbox" checked={c.done} onChange={() => toggleCheckDone(c._id)} />
                          <span className={c.done ? "line-through text-ink-400" : "text-ink-700"}>{c.label}</span>
                          <button onClick={() => removeCheckItem(c._id)} className="ml-auto text-ink-400 hover:text-red-500 text-xs">✕</button>
                        </li>
                      ))}
                    </ul>
                    <input
                      value={checkInput}
                      onChange={(e) => setCheckInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCheckItem())}
                      placeholder="Add an item and press Enter"
                      className="w-full mt-2 text-sm bg-transparent border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-blue-300"
                    />
                  </Section>
                )}

                {enabled.statusSummary && (
                  <Section title="Initial status summary" onRemove={() => toggleSection("statusSummary")} icon="📋">
                    <RichTextEditor
                      value={f.statusSummary}
                      onChange={(html) => setF((p) => ({ ...p, statusSummary: html }))}
                      placeholder="What's the starting state? Type @ to mention."
                      minHeight={80}
                      compact
                    />
                  </Section>
                )}

                {enabled.project && (
                  <Section title="Project" onRemove={() => toggleSection("project")} icon="📁">
                    <select
                      value={f.projectId}
                      onChange={(e) => setF({ ...f, projectId: e.target.value })}
                      className="w-full text-sm border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-blue-300"
                      disabled={!!defaultProjectId}
                    >
                      <option value="">No project</option>
                      {projects.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                  </Section>
                )}

                {enabled.participants && (
                  <Section title="Participants" onRemove={() => toggleSection("participants")} icon="👥">
                    <div className="text-xs text-ink-500 mb-2">Pick teammates to keep in the loop. They'll get notifications.</div>
                    <select
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) return;
                        if (!f.labels.includes(`@${id}`)) {
                          // we use labels array as a stand-in until real participants — to be wired in 10d
                        }
                        e.target.value = "";
                      }}
                      defaultValue=""
                      className="w-full text-sm border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-blue-300"
                    >
                      <option value="">+ Add participant</option>
                      {users.filter((u) => u._id !== f.assigneeId).map((u) => (
                        <option key={u._id} value={u._id}>{u.username}</option>
                      ))}
                    </select>
                    <div className="text-[11px] text-ink-400 mt-2">Watchers/observers wiring lands fully in 10d.</div>
                  </Section>
                )}

                {enabled.tags && (
                  <Section title="Tags" onRemove={() => toggleSection("tags")} icon="🏷️">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {f.labels.map((l) => (
                        <span key={l} className="inline-flex items-center px-2 py-0.5 text-[11px] rounded-full bg-ink-100 text-ink-700">
                          {l}
                          <button onClick={() => setF({ ...f, labels: f.labels.filter((x) => x !== l) })} className="ml-1 text-ink-400 hover:text-red-500">×</button>
                        </span>
                      ))}
                    </div>
                    <input
                      value={labelInput}
                      onChange={(e) => setLabelInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLabel())}
                      placeholder="Add a tag and press Enter"
                      className="w-full text-sm bg-transparent border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-blue-300"
                    />
                  </Section>
                )}

                {enabled.reminders && (
                  <Section title="Reminders" onRemove={() => toggleSection("reminders")} icon="🔔">
                    <div className="space-y-2">
                      <input
                        type="datetime-local"
                        value={f.reminderAt}
                        onChange={(e) => setF({ ...f, reminderAt: e.target.value })}
                        className="w-full text-sm border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-blue-300"
                      />
                      <input
                        type="text"
                        value={f.reminderBody}
                        onChange={(e) => setF({ ...f, reminderBody: e.target.value })}
                        placeholder="Optional reminder note"
                        className="w-full text-sm border border-ink-200 rounded px-2 py-1.5 outline-none focus:border-blue-300"
                      />
                      <p className="text-[11px] text-ink-400">In-app notifications fire automatically (cron from Phase 8b). Email/push channels in 10f.</p>
                    </div>
                  </Section>
                )}

                {enabled.deptSubtasks && (
                  <Section title="Department subtasks" onRemove={() => toggleSection("deptSubtasks")} icon="🎯">
                    <p className="text-xs text-ink-500 mb-2">Auto-create one subtask per department. Each can be assigned independently.</p>
                    <div className="space-y-1.5">
                      {FUNCTIONAL_DEPARTMENTS.map((d) => {
                        const enabledD = d in (f.departmentSubtasks || {});
                        return (
                          <div key={d} className="flex items-center gap-3 text-sm">
                            <label className="flex items-center gap-2 w-44">
                              <input type="checkbox" checked={enabledD} onChange={() => toggleDept(d)} />
                              <span className="w-2 h-2 rounded-full" style={{ background: FUNCTIONAL_ROLE_COLORS[d] }} />
                              <span>{FUNCTIONAL_ROLE_LABELS[d]}</span>
                            </label>
                            {enabledD && (
                              <select
                                value={f.departmentSubtasks[d] || ""}
                                onChange={(e) => setF({ ...f, departmentSubtasks: { ...f.departmentSubtasks, [d]: e.target.value } })}
                                className="text-xs border border-ink-200 rounded px-2 py-1 outline-none focus:border-blue-300"
                              >
                                <option value="">Unassigned</option>
                                {users.map((u) => <option key={u._id} value={u._id}>{u.username}</option>)}
                              </select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </Section>
                )}

                {enabled.files && (
                  <Section title="Files" onRemove={() => toggleSection("files")} icon="📎">
                    <div className="text-xs text-ink-500 italic">File uploads land in Phase 10c (storage decision pending).</div>
                  </Section>
                )}

                {enabled.timeTracking && (
                  <Section title="Time tracking" onRemove={() => toggleSection("timeTracking")} icon="⏱️">
                    <div className="text-xs text-ink-500 italic">Timer + manual logs land in Phase 10e. Schema is in place.</div>
                  </Section>
                )}

                {/* Section pills */}
                <div className="border-t border-ink-100 pt-3">
                  <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold mb-2">Add fields</div>
                  <div className="flex flex-wrap gap-1.5">
                    {SECTION_PILLS.map((p) => {
                      const on = enabled[p.key];
                      return (
                        <button
                          key={p.key}
                          onClick={() => toggleSection(p.key)}
                          className={[
                            "inline-flex items-center gap-1.5 px-3 py-1 text-[12px] rounded-full border transition",
                            on
                              ? "bg-blue-50 border-blue-300 text-blue-700"
                              : "bg-white border-ink-200 text-ink-600 hover:bg-ink-50",
                          ].join(" ")}
                        >
                          <span>{p.icon}</span>
                          <span>{p.label}</span>
                          {p.soon && <span className="text-[9px] text-ink-400">soon</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-3 border-t border-ink-100 flex items-center gap-3 bg-white">
                <button
                  onClick={submit}
                  disabled={submitting || !f.title.trim()}
                  className={[
                    "px-4 py-2 rounded-md text-sm font-semibold text-white transition",
                    submitting || !f.title.trim() ? "bg-ink-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600",
                  ].join(" ")}
                >
                  {submitting ? "Creating..." : "Create"}
                </button>
                <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-ink-600 hover:bg-ink-100">Cancel</button>
              </div>
            </div>

            {/* RIGHT — task chat panel (placeholder until 10d) */}
            <div className="hidden md:flex md:w-[40%] lg:w-[36%] flex-col bg-gradient-to-br from-blue-50 to-emerald-50">
              <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100 bg-white/60 backdrop-blur">
                <div>
                  <div className="text-sm font-semibold text-ink-800">Task chat</div>
                  <div className="text-[11px] text-ink-500">Available after task is created</div>
                </div>
                <span className="px-2 py-0.5 text-[10px] bg-ink-100 text-ink-500 rounded-full">Phase 10d</span>
              </div>

              <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-sm bg-white rounded-xl shadow-md p-6 text-center">
                  <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center text-2xl mb-3">💬</div>
                  <div className="text-lg font-semibold text-ink-800 mb-2">Task chat</div>
                  <ul className="text-sm text-ink-600 space-y-1.5 text-left">
                    <li>📞 Call chat members</li>
                    <li>📎 Share documents and files</li>
                    <li>📊 Discuss progress and results</li>
                    <li>📈 Track task updates</li>
                  </ul>
                </div>
              </div>

              <div className="px-4 py-3 border-t border-ink-100 bg-white/60">
                <div className="bg-white rounded-md border border-ink-200 px-3 py-2 text-xs text-ink-400">
                  Type @ or + to mention a person, a chat or AI...
                </div>
              </div>
            </div>

          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-3">
      <span className="text-xs text-ink-500 font-medium">{label}:</span>
      <div>{children}</div>
    </div>
  );
}

function Section({ title, children, onRemove, icon }) {
  return (
    <section className="bg-white border border-ink-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-700">
          <span>{icon}</span>
          <span>{title}</span>
        </div>
        {onRemove && (
          <button onClick={onRemove} className="text-ink-400 hover:text-ink-600 text-xs">remove</button>
        )}
      </div>
      {children}
    </section>
  );
}
